/**
 * OCR Date Parser
 *
 * Handles various date formats from OCR with intelligent pattern inference.
 * OCR often returns inconsistent formats or missing characters.
 */

import { isValid, parse, parseISO } from "date-fns";

/**
 * Parse OCR date string to ISO format
 *
 * Handles:
 * - MM/dd/yyyy (01/15/1990)
 * - MM/dd yyyy (01/15 1990 - missing second /)
 * - MM-dd-yyyy (01-15-1990)
 * - yyyy-MM-dd (1990-01-15)
 * - M/d/yyyy (1/15/1990)
 * - Mixed separators and missing characters
 *
 * @param dateStr - Date string from OCR
 * @returns ISO date string (1990-01-15T00:00:00.000Z) or undefined
 */
export function parseOcrDate(
  dateStr: string | null | undefined,
): string | undefined {
  if (!dateStr) return undefined;

  try {
    // Normalize: remove extra spaces, standardize separators
    const normalized = dateStr.trim().replace(/\s+/g, " ");

    // Detect separator type (slash, dash, or space)
    const hasSeparator = /[\/\-\s]/.test(normalized);
    if (!hasSeparator) {
      console.warn("No separator detected in OCR date:", dateStr);
      return undefined;
    }

    // Extract numbers from the date string
    const numbers = normalized.match(/\d+/g);
    if (!numbers || numbers.length < 3) {
      console.warn("Insufficient date components in OCR date:", dateStr);
      return undefined;
    }

    // Infer format based on number patterns
    const [first, second, third] = numbers.map((n) => Number.parseInt(n, 10));

    let date: Date | undefined;

    // YYYY-MM-DD or YYYY/MM/DD or YYYY MM DD (year first)
    if (first > 1900 && first < 2100) {
      date = new Date(Date.UTC(first, second - 1, third));
    }
    // DD-MM-YYYY or DD/MM/YYYY (EU format - day first, month ≤ 12)
    else if (first > 12 && third > 1900 && third < 2100) {
      date = new Date(Date.UTC(third, second - 1, first));
    }
    // MM-DD-YYYY or MM/DD/YYYY or MM DD YYYY (US format - month first)
    else if (third > 1900 && third < 2100) {
      date = new Date(Date.UTC(third, first - 1, second));
    }

    // Validate
    if (date && isValid(date)) {
      return date.toISOString();
    }

    // Fallback: Try structured parsing with date-fns
    // date-fns parse()/parseISO() create local-time dates, so normalize to midnight UTC
    date = tryStructuredParsing(normalized);
    if (date && isValid(date)) {
      return new Date(
        Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()),
      ).toISOString();
    }

    console.warn("Could not parse OCR date:", dateStr);
    return undefined;
  } catch (error) {
    console.error("Error parsing OCR date:", dateStr, error);
    return undefined;
  }
}

/**
 * Try parsing with explicit date-fns formats
 */
function tryStructuredParsing(dateStr: string): Date | undefined {
  const formats = [
    "MM/dd/yyyy", // 01/15/1990
    "MM-dd-yyyy", // 01-15-1990
    "yyyy-MM-dd", // 1990-01-15
    "yyyy/MM/dd", // 1990/01/15
    "M/d/yyyy", // 1/15/1990
    "M-d-yyyy", // 1-15-1990
    "dd/MM/yyyy", // 15/01/1990 (EU)
    "dd-MM-yyyy", // 15-01-1990 (EU)
    "MM dd yyyy", // 01 15 1990 (space separated)
    "yyyy MM dd", // 1990 01 15 (space separated)
  ];

  for (const format of formats) {
    try {
      const date = parse(dateStr, format, new Date());
      if (isValid(date)) {
        return date;
      }
    } catch {
      // Try next format
    }
  }

  // Last resort: try parseISO
  try {
    const date = parseISO(dateStr);
    if (isValid(date)) {
      return date;
    }
  } catch {
    // Failed
  }

  return undefined;
}
